import { FormBuilder, FormControl, Validators } from '@angular/forms';
import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  OnChanges
} from '@angular/core';
import {
  trigger,
  state,
  style,
  transition,
  animate
} from '@angular/animations';
import { ControlRegisterSections } from '../../../abstration/control-register-sections';
import { FormGroup } from '@angular/forms';
import { PH3AService } from '../../../services/PH3A.service';
import { RegistrationService } from '../../../services/registration.service';
import { Store } from '@ngxs/store';
import { DomainService } from '../../../services/domain.service';
import { ToastyService } from 'ng2-toasty';
import { LoadingService } from '../../../services/loading.service';
import swal from 'sweetalert2';
import { AgentDocument } from '../../../models/agents/agent-document';
import { RepresentativesData } from '../../../models/agents/representatives-data';
import { RxFormBuilder } from '@rxweb/reactive-form-validators';
import { FormHelper } from '../../../helpers/form-helper';
import { AgentRegister } from '../../../models/agents/agent-register';
import { PersonType } from '../../../models/enums/person-type.enum';
import { InvestmentFundRepresentativesDataModel } from './personas/investment-fund-representative.model';
import { InvestorCompanyRepresentativesDataModel } from './personas/investor-company-representative.model';
import { IssuerRepresentativesDataModel } from './personas/issuer-representative.model';
import { ManagerRepresentativesDataModel } from './personas/manager-representative.model';
import { BaseRepresentativesDataModel } from './personas/base.model';
import { RegisterOriginType } from '../../../models/enums/register-origin-type-enum';
import { CompanyService } from '../../../services/company.service';
import { RepresentativeType } from '../../../models/enums/representative-type.enum';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-representative-form',
  templateUrl: './representative-form.component.html',
  styleUrls: ['./representative-form.component.scss'],
  animations: [
    trigger('simpleFadeAnimation', [
      state('in', style({ opacity: 1 })),
      transition(':enter', [style({ opacity: 0 }), animate(600)]),
      transition(':leave', animate(600, style({ opacity: 0 })))
    ])
  ]
})
export class RepresentativeFormComponent
  extends ControlRegisterSections
  implements OnInit, OnChanges {
  @Input() defaultValue: RepresentativesData;
  @Input() allowEditing = true;
  @Output() onAdd = new EventEmitter<RepresentativesData>();
  @Output() onDelete = new EventEmitter<RepresentativesData>();
  @Output() onClose = new EventEmitter();
  @Output() onUpdatePortalAccess = new EventEmitter<RepresentativesData>();
  @Input() agent: AgentRegister;

  formGroup: FormGroup;
  title: string;
  headerContent: string;
  saveButtonDisabled = false;

  representativeTypes: RepresentativeType[] = Object.values(RepresentativeType);
  representativeTypeValueChangesSubscription: Subscription;

  constructor(
    protected registrationService: RegistrationService,
    protected phService: PH3AService,
    protected store: Store,
    protected domainService: DomainService,
    protected toastyService: ToastyService,
    protected loadingService: LoadingService,
    protected formBuilder: FormBuilder,
    protected representativeData: RepresentativesData,
    protected rxFormBuilder: RxFormBuilder,
    protected companyService: CompanyService
  ) {
    super(formBuilder, toastyService, companyService);
  }

  ngOnInit() {
    if (this.defaultValue) {
      this.formGroup.patchValue({ ...this.defaultValue });
      this.onPoliticalyExposedChange(this.defaultValue.isPoliticallyExposed);
    }

    this.setCardTitle();
    this.setCardHeaderContent();
  }

  ngOnChanges(): void {
    const formatHelper = new FormHelper();
    let model: BaseRepresentativesDataModel;

    if (this.representativeTypeValueChangesSubscription) {
      this.representativeTypeValueChangesSubscription.unsubscribe();
    }

    switch (this.agent.personType) {
      case PersonType.Emissor:
        model = new IssuerRepresentativesDataModel();
        model.registerOriginType = this.agent.registerOriginType;
        this.formGroup = formatHelper.ExtractFormGroupFromModel(
          model,
          IssuerRepresentativesDataModel
        );
        break;
      case PersonType.Juridica:
        model = new InvestmentFundRepresentativesDataModel();
        model.registerOriginType = this.agent.registerOriginType;
        this.formGroup = formatHelper.ExtractFormGroupFromModel(
          model,
          InvestmentFundRepresentativesDataModel
        );
        break;
      case PersonType.PJInvestidor:
        model = new InvestorCompanyRepresentativesDataModel();
        model.registerOriginType = this.agent.registerOriginType;
        this.formGroup = formatHelper.ExtractFormGroupFromModel(
          model,
          InvestorCompanyRepresentativesDataModel
        );
        break;
      case PersonType.Gestor:
        model = new ManagerRepresentativesDataModel();
        model.registerOriginType = this.agent.registerOriginType;
        this.formGroup = formatHelper.ExtractFormGroupFromModel(
          model,
          ManagerRepresentativesDataModel
        );
        break;
      default:
        break;
    }

    const representativeType = this.formGroup.get('representativeType');

    this.representativeTypeValueChangesSubscription =
      representativeType.valueChanges.subscribe((representativeType) => {
        if (
          representativeType !== RepresentativeType.SOCIO_REPRESENTANTE_LEGAL
        ) {
          this.formGroup.get('percentageShareholding').setValue(null);
        }
      });
  }

  setSaveButtonDisabled(value: boolean) {
    this.saveButtonDisabled = value;
  }

  isTokenizadora() {
    return (
      this.agent.registerOriginType ===
      RegisterOriginType[RegisterOriginType.Tokenizadora]
    );
  }

  isFundOrCompany() {
    return [PersonType.Juridica, PersonType.PJInvestidor].includes(
      this.agent.personType
    );
  }

  setCardTitle() {
    if (this.defaultValue && this.defaultValue.id) {
      const { name } = this.defaultValue;
      this.title = name;
    } else {
      this.title = 'Novo Representante';
    }
  }

  setCardHeaderContent() {
    if (this.defaultValue && this.defaultValue.id) {
      const { socialNumber } = this.defaultValue;
      const document = new AgentDocument(socialNumber);
      this.headerContent = document.getFormated();
    } else {
      this.headerContent =
        'Informe os dados abaixo para cadastrar um novo representante';
    }
  }

  toUpperCase(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    this.formGroup
      .get('nationalDocument')
      .setValue(input.value.toLocaleUpperCase());
  }

  socialNumberValidator(control: FormControl) {
    const document = new AgentDocument(control.value);
    if (!document.isValid()) {
      return { validCpf: true };
    }
    return null;
  }

  nationalDocumentValidator(control: FormControl) {
    const value = control.value as string;
    if (value && isNaN(Number(value)) && value[value.length - 1] !== 'X') {
      return { validNationalDocument: true };
    }
    return null;
  }

  initReactiveForms(): void { }

  addRepresentative() {
    const agent = new AgentDocument(this.formGroup.get('socialNumber').value);

    if (!agent.isValid()) {
      this.invalidDocument();
      return;
    }

    if (!this.isValidForm()) {
      this.validadeAllFormGroupWithMessage();
      return;
    }

    const representative = <RepresentativesData>this.formGroup.value;
    this.onAdd.emit(representative);
  }

  invalidDocument() {
    this.addToast({
      title: '',
      msg: 'CPF inválido',
      showClose: true,
      timeout: 5000,
      theme: 'bootstrap',
      type: 'error',
      position: 'bottom-center',
      closeOther: true
    });
  }

  deleteRepresentative() {
    this.onDelete.emit(this.defaultValue);
  }

  searchRepresentative() {
    this.setSaveButtonDisabled(false);
    const document = this.formGroup.get('socialNumber').value;
    this.loadingService.isLoading(true);

    const _handlePerson = (person) => {
      if (person) {
        const name = person.person.fullName;
        this.formGroup.get('name').setValue(name);
      }
      this.loadingService.isLoading(false);
    };

    const _handlePersonError = () => {
      swal({
        title: 'Ops!',
        text: 'Não foi possível realizar a consulta do CPF informado',
        type: 'error'
      }).catch(swal.noop);
      this.loadingService.isLoading(false);
    };

    this.phService
      .getPersonData(document)
      .subscribe(_handlePerson, _handlePersonError);
  }

  onPoliticalyExposedChange(isExposed) {
    const politicallyExposed = this.formGroup.get(
      'politicallyExposed'
    ) as FormGroup;

    if (isExposed) {
      politicallyExposed.enable();
    } else {
      politicallyExposed.disable();
    }
  }

  onMeetingRepresentantChange() {
    const checked = this.formGroup.get('isMeetingRepresentant').value;

    if (checked) {
      this.formGroup.patchValue({ portalAccess: true });
    }
  }

  onPortalAccessChange() {
    const checked = this.formGroup.get('portalAccess').value;

    if (!checked) {
      this.formGroup.patchValue({ isMeetingRepresentant: false });
    }
  }

  showControlPercentageShareholding(): boolean {
    if (this.formGroup.contains('percentageShareholding')) {
      const representativeType = this.formGroup.get('representativeType');

      return (
        representativeType &&
        representativeType.value ===
        RepresentativeType.SOCIO_REPRESENTANTE_LEGAL
      );
    }

    return false;
  }

  fieldAvailable(field: string): boolean {
    const isEscrow =
      this.agent.isEscrowTitular() || this.agent.isEscrowPartesRelacionadas();

    const isBancoLiquidante =
      this.agent.isBancoLiquidantePartesRelacionadas() ||
      this.agent.isBancoLiquidanteTitular();

    if (!isEscrow && !isBancoLiquidante) {
      const fieldsEscrowBancoLiquidante = [
        'masterAccess',
        'representativeType',
        'phone'
      ];

      if (fieldsEscrowBancoLiquidante.includes(field)) return false;
    }

    if (!isEscrow) {
      const fieldsEscrow = ['representativeType'];

      if (fieldsEscrow.includes(field)) return false;
    }

    if (!this.agent.isFundo()) {
      const fieldsFundos = ['isMeetingRepresentant'];

      if (fieldsFundos.includes(field)) return false;
    }

    return true;
  }

  get isEscrowOrBancoLiquidante() {
    return (
      this.agent &&
      (this.agent.isBancoLiquidanteTitular() ||
        this.agent.isEscrowTitular() ||
        this.agent.isEscrowPartesRelacionadas() ||
        this.agent.isBancoLiquidantePartesRelacionadas())
    );
  }
}
